import { Component } from '@theme/component';

/**
 * @typedef {Object} ChatButtonRefs
 * @property {HTMLElement} chatButton - The button to toggle the chat dialog
 */

/**
 * A custom element that provides a chat button
 *
 * @extends {Component<ChatButtonRefs>}
 */
class ChatButtonComponent extends Component {
  requiredRefs = ['chatButton'];

  #chatService = null;

  /**
   * Lazily load and instantiate ChatService on first use.
   */
  async #getService() {
    if (!this.#chatService) {
      const { default: ChatService } = await import('./chat-service.js');
      this.#chatService = new ChatService();
    }
    return this.#chatService;
  }

  /**
   * Initialize the chat service with default configuration
   */
  async initializeChatService() {
    try {
      const dimensions = this.getChatDimensions();
      await (await this.#getService()).initialize({ dimensions });
    } catch (error) {
      console.warn('Failed to initialize chat service:', error);
    }
  }

  /**
   * Get chat dimensions from data attributes or use defaults
   */
  getChatDimensions() {
    const width = this.dataset.chatWidth || '400px';
    const height = this.dataset.chatHeight || '600px';

    return {
      width,
      height,
    };
  }

  /**
   * Public method to show chat programmatically
   */
  async showChat() {
    return (await this.#getService()).showChat();
  }

  /**
   * Public method to hide chat programmatically
   */
  async hideChat() {
    return (await this.#getService()).hideChat();
  }

  /**
   * Check if chat is available
   */
  async isChatAvailable() {
    return (await this.#getService()).isChatAvailable();
  }

  /**
   * Get current chat state
   */
  async getChatState() {
    return (await this.#getService()).getChatState();
  }
}

if (!customElements.get('chat-button')) {
  customElements.define('chat-button', ChatButtonComponent);
}
